import { useEffect, useState, type ReactNode } from "react";
import { decideLanding, isCampaignPageLoad } from "../../lib/landingGate";
import type { LandingFlow } from "../../lib/landingFlow";

/**
 * Routes paid-social landings into the collaboration first run.
 *
 * Only page loads that look like a campaign landing pay any cost here —
 * every other visitor renders `children` synchronously on the first
 * frame, exactly as before. Campaign visitors see a neutral splash until
 * auth resolves (or the timeout fires), then either the standard app
 * (signed-in / room in progress / flag off) or the seeded starter room.
 *
 * The decision itself lives in `lib/landingGate.ts` (`decideLanding`),
 * which also performs the one side effect (`enterCollabOnboarding`)
 * before the tree mounts, so the canvas never flashes empty first.
 */
export function CampaignLandingGate({ children }: { children: ReactNode }) {
  const [isCampaign] = useState(() => isCampaignPageLoad());
  if (!isCampaign) return <>{children}</>;
  return <Gate>{children}</Gate>;
}

function Gate({ children }: { children: ReactNode }) {
  const [decision, setDecision] = useState<LandingFlow | null>(null);

  useEffect(() => {
    let cancelled = false;
    void decideLanding().then((flow) => {
      if (!cancelled) setDecision(flow);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!decision) return <LandingSplash />;
  return <>{children}</>;
}

/** Neutral branded splash — no copy that could contradict either flow. */
export function LandingSplash() {
  return (
    <div
      className="fixed inset-0 z-[70] bg-ink-50 flex items-center justify-center"
      role="status"
      aria-label="Loading Room Canvas"
      data-testid="landing-splash"
    >
      <div className="flex flex-col items-center gap-3 animate-pulse">
        <div className="size-12 rounded-2xl bg-ink-900 text-white flex items-center justify-center">
          <svg viewBox="0 0 32 32" className="size-7">
            <path
              d="M6 10 L20 10 L20 6 L26 6 L26 22 L18 22 L18 26 L6 26 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <circle cx="13" cy="18" r="1.6" fill="currentColor" />
          </svg>
        </div>
        <div className="text-body font-semibold text-ink-900">Room Canvas</div>
      </div>
    </div>
  );
}
